import { Injectable } from '@angular/core';
import { DatabaseService } from './base/database.service';
import { QuestionnaireAnswer, Answer } from '../models/questionnaire.model';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { AddressValidationPayload, StructuredAddress } from './address.service';

export interface ConfirmQuestionnaireAddressResponse {
  details: { payload: AddressValidationPayload; address?: StructuredAddress };
}

@Injectable({
  providedIn: 'root'
})
export class QuestionnaireAnswerService  extends DatabaseService<QuestionnaireAnswer> {
  constructor(private http: HttpClient) {
    super('cp_questionnaire_answer');
  }

  async listQuestionnaireAnswerFromClaim(claim_id:string) {
    const a = await this.supabase.from(this.table).select('*').eq('claim_id',claim_id);

    if (!a.data || a.error) {
      console.error('[QuestionnaireAnswerService] listQuestionnaireAnswerFromClaim', {response:a, claim_id});
      throw 'Error fetching data';
    }

    return a.data as unknown as QuestionnaireAnswer[];

  }

  /**
   * Merge the provided answersPartial into the existing answers JSON for the
   * QuestionnaireAnswer with id=answerId and update only the `answers` column.
   * Returns the updated QuestionnaireAnswer record.
   */
  async updateAnswersPartial(
    answerId: string,
    answersPartial: { [slug: string]: Answer }
  ): Promise<QuestionnaireAnswer> {
    // read current answers
    const get = await this.supabase
      .from(this.table)
      .select('answers')
      .eq('id', answerId)
      .single();

    if (!get.data || get.error) {
      console.error('[QuestionnaireAnswerService] updateAnswersPartial read', {
        response: get,
        answerId,
      });
      throw 'Error reading questionnaire answer';
    }

    const currentAnswers = (get.data as any).answers || {};
    const merged = { ...currentAnswers, ...answersPartial };

    const { data, error } = await this.supabase
      .from(this.table)
      .update({ answers: merged })
      .eq('id', answerId)
      .select()
      .single();

    if (error) {
      console.error('[QuestionnaireAnswerService] updateAnswersPartial update', {
        error,
        answerId,
        answersPartial,
      });
      throw 'Error updating questionnaire answers';
    }

    return data as QuestionnaireAnswer;
  }

  /**
   * Convenience helper: update a single question (by slug) inside the answers
   * object. `answerValue` must follow the Answer interface shape.
   */
  async updateSingleAnswer(
    answerId: string,
    questionSlug: string,
    answerValue: Answer
  ): Promise<QuestionnaireAnswer> {
    const partial: { [slug: string]: Answer } = {};
    partial[questionSlug] = answerValue;
    return this.updateAnswersPartial(answerId, partial);
  }

  /**
   * Submit questionnaire answers by updating status to SUBMITTED and setting completed_at
   */
  async submitQuestionnaireAnswer(answerId: string): Promise<QuestionnaireAnswer> {
    const { data, error } = await this.supabase
      .from(this.table)
      .update({ 
        status: 'SUBMITTED',
        completed_at: new Date().toISOString()
      })
      .eq('id', answerId)
      .select()
      .single();

    if (error) {
      console.error('[QuestionnaireAnswerService] submitQuestionnaireAnswer', {
        error,
        answerId,
      });
      throw 'Error submitting questionnaire answer';
    }

    return data as QuestionnaireAnswer;
  }

  /**
   * Update completed questionnaire answer using lambda endpoint
   * @param questionnaireAnswerId - ID of the questionnaire answer
   * @param answers - Object containing the answers to update { [key: string]: any }
   * @returns Updated QuestionnaireAnswer
   */
  async updateCompletedQuestionnaireAnswer(
    questionnaireAnswerId: string,
    answers: { [key: string]: any }
  ): Promise<QuestionnaireAnswer> {
    const body = {
      questionnaire_answer_id: questionnaireAnswerId,
      answers: answers
    };

    const token = (await this.supabase.auth.getSession()).data.session?.access_token;

    const ret = (await firstValueFrom(
      this.http.put(environment.lambdaUrl + '/update_completed_questionnaire_answer', body, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    )) as any;

    // Retorna a resposta completa para que o componente possa verificar o status
    return ret;
  }

  /**
   * Submit full questionnaire answer using lambda endpoint
   * Marks the questionnaire as SUBMITTED and sends all data
   * @param questionnaireAnswer - Complete QuestionnaireAnswer object
   * @returns Submitted QuestionnaireAnswer
   */
  async submitFullQuestionnaire(questionnaireAnswer: QuestionnaireAnswer): Promise<QuestionnaireAnswer> {
    const token = (await this.supabase.auth.getSession()).data.session?.access_token;

    const ret = (await firstValueFrom(
      this.http.post(
        environment.lambdaUrl + '/submit_questionnaire_answer',
        questionnaireAnswer,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
    )) as any;

    return ret;
  }

  /**
   * CPK-4239: persists the outcome of the address-confirmation modal opened
   * after a questionnaire submit flags/fails an address.
   */
  async confirmAddressValidation(
    questionnaireAnswerId: string,
    questionSlug: string,
    action: 'confirmed_override' | 'modified_address',
    address?: StructuredAddress
  ): Promise<ConfirmQuestionnaireAddressResponse> {
    const token = (await this.supabase.auth.getSession()).data.session?.access_token;

    return firstValueFrom(
      this.http.post<ConfirmQuestionnaireAddressResponse>(
        environment.lambdaUrl + '/confirm_questionnaire_address',
        {
          questionnaire_answer_id: questionnaireAnswerId,
          question_slug: questionSlug,
          action,
          address,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
    );
  }

}
